import { makeAutoObservable, runInAction } from 'mobx';

import { Department, DepartmentCodes, findDepartmentById } from '../models/Department';
import { getEffectiveUserDepartmentMemberships, getUserDepartments, User } from '../models/User';
import { fetchCurrentDepartment, fetchDepartments } from '../services/DepartmentsAPI';
import { getCurrentHostName, setActiveDepartmentCode } from '../utils/ActiveDepartment';
import { errorStore } from './ErrorStore';
import { invoiceStore } from './InvoiceStore';
import { invoicePaymentTypeStore } from './InvoicePaymentTypeStore';
import { vendorStore } from './VendorStore';
import { categoryStore } from './CategoryStore';
import { splitStore } from './SplitStore';
import { accountStore } from './AccountStore';
import { customerStore } from './CustomerStore';
import { locationStore } from './LocationStore';
import { exportLogStore } from './ExportLogStore';

const ACTIVE_DEPARTMENT_STORAGE_KEY = 'quikfin.activeDepartmentId';

class DepartmentStore {
	availableDepartments: Department[] = [];
	activeDepartment: Department | undefined;
	hostDepartment: Department | undefined;
	currentUser: User | undefined;
	contextError: string | undefined;
	isLoading = false;

	constructor() {
		makeAutoObservable(this);
	}

	async initialize(user?: User) {
		this.currentUser = user;
		await this.loadServerContext(user);
	}

	async loadAvailableDepartments() {
		try {
			const departments = await fetchDepartments();
			if (departments?.length) {
				this.setAvailableDepartments(departments);
			}
		} catch (error) {
			console.warn('Department list is not available yet.', error);
		}
	}

	switchDepartment(departmentId: number) {
		const department = this.allowedDepartments.find(row => row.id === departmentId);

		if (!department || department.id === this.activeDepartment?.id) {
			return;
		}

		this.setActiveDepartment(department);
		localStorage.setItem(ACTIVE_DEPARTMENT_STORAGE_KEY, String(department.id));
	}

	get allowedDepartments() {
		return getUserDepartments(this.currentUser, this.availableDepartments);
	}

	get isPrologue() {
		return this.activeDepartment?.code === DepartmentCodes.Prologue;
	}

	private async loadServerContext(user?: User) {
		this.setLoading(true);
		this.setContextError(undefined);

		try {
			const context = await fetchCurrentDepartment(getCurrentHostName());
			runInAction(() => {
				const departments = mergeDepartments(this.availableDepartments, context.allowedDepartments);
				const hostDepartment = findDepartmentById(context.activeDepartmentId, departments);
				const activeDepartment = this.resolveActiveDepartment(user, hostDepartment, departments);
				this.setAvailableDepartments(departments);
				this.hostDepartment = hostDepartment;
				if (activeDepartment) {
					this.setActiveDepartment(activeDepartment);
				}
			});
		} catch (error) {
			const message = error instanceof Error
				? error.message
				: 'Department context is not available yet.';
			this.clearDepartmentContext();
			this.setContextError(message);
			errorStore.report(message);
			console.warn('Department context is not available yet.', error);
		} finally {
			this.setLoading(false);
		}
	}

	private resolveActiveDepartment(user: User | undefined, hostDepartment: Department | undefined, departments: Department[]) {
		const memberships = getEffectiveUserDepartmentMemberships(user);
		const allowedDepartments = getUserDepartments(user, departments);
		const storedDepartmentId = Number(localStorage.getItem(ACTIVE_DEPARTMENT_STORAGE_KEY));
		const storedDepartment = findDepartmentById(storedDepartmentId, allowedDepartments);
		const defaultMembership = memberships.find(membership => membership.isDefault);
		const defaultDepartment = defaultMembership
			? findDepartmentById(defaultMembership.departmentId, allowedDepartments)
			: undefined;

		if (storedDepartment) {
			return storedDepartment;
		}

		localStorage.removeItem(ACTIVE_DEPARTMENT_STORAGE_KEY);

		return allowedDepartments.find(department => department.id === hostDepartment?.id)
			?? defaultDepartment
			?? allowedDepartments[0]
			?? hostDepartment;
	}

	setAvailableDepartments(departments: Department[]) {
		this.availableDepartments = departments;
	}

	setActiveDepartment(department: Department) {
		const departmentChanged = this.activeDepartment?.id !== department.id;
		this.activeDepartment = department;
		setActiveDepartmentCode(department.code);

		if (departmentChanged) {
			exportLogStore.resetStore();
			invoiceStore.resetForDepartmentChange();
			void invoicePaymentTypeStore.reloadForActiveDepartment();
			void vendorStore.reloadForActiveDepartment();
			void categoryStore.reloadForActiveDepartment();
			void accountStore.reloadForActiveDepartment();
			void customerStore.reloadForActiveDepartment();
			void locationStore.reloadForActiveDepartment();
			void splitStore.reloadForActiveDepartment();
		}
	}

	setLoading(loading: boolean) {
		this.isLoading = loading;
	}

	setContextError(error: string | undefined) {
		this.contextError = error;
	}

	clearDepartmentContext() {
		exportLogStore.resetStore();
		this.activeDepartment = undefined;
		this.hostDepartment = undefined;
		setActiveDepartmentCode();
	}
}

const mergeDepartments = (currentDepartments: Department[], incomingDepartments: Department[]) => {
	const departmentMap = new Map<number, Department>();

	currentDepartments.forEach(department => departmentMap.set(department.id, department));
	incomingDepartments.forEach(department => departmentMap.set(department.id, department));

	return Array.from(departmentMap.values());
};

export const departmentStore = new DepartmentStore();
